import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { rfqService, type FiltrosRfq } from '../services/rfqService';
import type {
  StatusRfq,
  CriarRfqRequest,
  AtualizarRfqRequest,
  AdjudicarRfqRequest,
  RegistrarRespostaFornecedorRequest,
} from '../types';

export const useRfq = (params?: { pagina?: number; tamanhoPagina?: number; status?: StatusRfq; filtros?: FiltrosRfq }) => {
  const qc = useQueryClient();
  const pagina        = params?.pagina        ?? 1;
  const tamanhoPagina = params?.tamanhoPagina ?? 20;
  const status        = params?.status;
  const filtros       = params?.filtros ?? {};

  const query = useQuery({
    queryKey: ['rfq', pagina, tamanhoPagina, status, filtros],
    queryFn:  () => rfqService.getAll(pagina, tamanhoPagina, status, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });

  const criarMut = useMutation({
    mutationFn: (data: CriarRfqRequest) => rfqService.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfq'] }),
  });

  const atualizarMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarRfqRequest }) =>
      rfqService.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      qc.invalidateQueries({ queryKey: ['rfq', id] });
    },
  });

  const enviarMut = useMutation({
    mutationFn: (id: number) => rfqService.enviar(id),
    onSuccess: ({ emails }) => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      toast.success(`Convites enviados: ${emails.enviados}/${emails.total} e-mail(s).`);
      if (emails.falhas.length > 0) {
        toast.warning('Alguns convites não foram enviados', {
          description: emails.falhas.join('\n'),
        });
      }
    },
    onError: (error) => {
      const falhas = (error as { response?: { data?: { emails?: { falhas?: string[] } } } })
        ?.response?.data?.emails?.falhas;
      toast.error(
        getApiErrorMessage(error, 'Falha ao enviar os convites.'),
        falhas?.length ? { description: falhas.join('\n') } : undefined,
      );
    },
  });

  const encerrarMut = useMutation({
    mutationFn: (id: number) => rfqService.encerrar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfq'] }),
  });

  const adjudicarMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdjudicarRfqRequest }) =>
      rfqService.adjudicar(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['rfq'] });
      qc.invalidateQueries({ queryKey: ['rfq', id] });
    },
  });

  const registrarRespostaMut = useMutation({
    mutationFn: ({ id, fornecedorId, data }: {
      id: number;
      fornecedorId: number;
      data: RegistrarRespostaFornecedorRequest;
    }) => rfqService.registrarResposta(id, fornecedorId, data),
    onSuccess: (_, { id }) => qc.invalidateQueries({ queryKey: ['rfq', id] }),
  });

  return {
    rfqs:          query.data?.itens         ?? [],
    total:         query.data?.total         ?? 0,
    totalPaginas:  query.data?.totalPaginas  ?? 1,
    porStatus:     query.data?.porStatus,
    comRespostas:  query.data?.comRespostas,
    isFetching:    query.isFetching,
    isLoading:     query.isLoading,
    criarRfq:      criarMut.mutateAsync,
    atualizarRfq:  atualizarMut.mutateAsync,
    enviarRfq:     enviarMut.mutate,
    encerrarRfq:   encerrarMut.mutateAsync,
    adjudicarRfq:  adjudicarMut.mutateAsync,
    registrarResposta: registrarRespostaMut.mutateAsync,
    isCreating:    criarMut.isPending,
    isUpdating:    atualizarMut.isPending,
    isEnviando:    enviarMut.isPending,
    isEnccerrando: encerrarMut.isPending,
    isAdjudicando: adjudicarMut.isPending,
  };
};

export const useRfqDetalhe = (id: number) =>
  useQuery({
    queryKey: ['rfq', id],
    queryFn:  () => rfqService.getById(id),
    enabled:  id > 0,
  });

export const useRfqComparativo = (id: number) =>
  useQuery({
    queryKey: ['rfq', id, 'comparativo'],
    queryFn:  () => rfqService.comparativo(id),
    enabled:  id > 0,
  });
